import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { AiResult } from '../../models/AiResult'
import { RootState } from '../store'

interface AiResultsState {
  isLoading: boolean
  aiResults: EntityState<AiResult, string>
  editedAiResults: EntityState<AiResult, string>
  pollingEnabled: boolean
  isResultSaveLoading: boolean
  isResultScoreLoading: boolean
  currentRerunId: string | null
  isRerunLoading: boolean
  savedResultId: string | null
}

const aiResultsAdapter = createEntityAdapter<AiResult>()
const editedAiResultsAdapter = createEntityAdapter<AiResult>()

const initialState: AiResultsState = {
  isLoading: true,
  aiResults: aiResultsAdapter.getInitialState(),
  editedAiResults: editedAiResultsAdapter.getInitialState(),
  pollingEnabled: false,
  isResultSaveLoading: false,
  isResultScoreLoading: false,
  currentRerunId: null,
  isRerunLoading: false,
  savedResultId: null,
}

export const aiResultsSlice = createSlice({
  name: 'aiResults',
  initialState,
  reducers: {
    setAiResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setAiResultSaveLoading: (state, action: PayloadAction<boolean>) => {
      state.isResultSaveLoading = action.payload
    },
    setAiResultScoreLoading: (state, action: PayloadAction<boolean>) => {
      state.isResultScoreLoading = action.payload
    },
    setAiResultRerunLoading: (state, action: PayloadAction<{ isLoading: boolean; rerunId: string }>) => {
      const { isLoading, rerunId } = action.payload
      if (isLoading) {
        state.isRerunLoading = true
        state.currentRerunId = rerunId
      } else if (state.currentRerunId === rerunId) {
        state.isRerunLoading = false
        state.currentRerunId = null
      }
    },
    setSavedResultId: (state, action: PayloadAction<string | null>) => {
      state.savedResultId = action.payload
    },
    addAiResults: (state, action: PayloadAction<AiResult[]>) => {
      aiResultsAdapter.addMany(state.aiResults, action.payload)
    },
    addAiResult: (state, action: PayloadAction<AiResult>) => {
      aiResultsAdapter.addOne(state.aiResults, action.payload)
    },
    setAiResult: (state, action: PayloadAction<AiResult>) => {
      aiResultsAdapter.setOne(state.aiResults, action.payload)
    },
    setEditedAiResult: (state, action: PayloadAction<AiResult>) => {
      editedAiResultsAdapter.setOne(state.editedAiResults, action.payload)
    },
    removeEditedAiResult: (state, action: PayloadAction<string>) => {
      editedAiResultsAdapter.removeOne(state.editedAiResults, action.payload)
    },
    removeEditedAiResults: (state) => {
      editedAiResultsAdapter.removeAll(state.editedAiResults)
    },
    setAiResults: (state, action: PayloadAction<AiResult[]>) => {
      aiResultsAdapter.setMany(state.aiResults, action.payload)
    },
    startAiResultPolling: (state) => {
      state.pollingEnabled = true
    },
    stopAiResultPolling: (state) => {
      state.pollingEnabled = false
    },
    resetAiResults: () => initialState,
  },
})

export const {
  setAiResultsLoading,
  setAiResultSaveLoading,
  setAiResultScoreLoading,
  setAiResultRerunLoading,
  setSavedResultId,
  setEditedAiResult,
  removeEditedAiResult,
  removeEditedAiResults,
  addAiResults,
  addAiResult,
  setAiResult,
  setAiResults,
  startAiResultPolling,
  stopAiResultPolling,
  resetAiResults,
} = aiResultsSlice.actions

const aiResultsSelectors = aiResultsAdapter.getSelectors((state: RootState) => state.meetingSummary.aiResults.aiResults)
const editedAiResultsSelectors = editedAiResultsAdapter.getSelectors((state: RootState) => state.meetingSummary.aiResults.editedAiResults)

export const selectAiResults = (state: RootState) => aiResultsSelectors.selectAll(state)

export const selectAiResultSaveLoading = (state: RootState) => state.meetingSummary.aiResults.isResultSaveLoading

export const selectAiResultsLoading = (state: RootState) => state.meetingSummary.aiResults.isLoading

export const selectAiResultScoreLoading = (state: RootState) => state.meetingSummary.aiResults.isResultScoreLoading

export const selectAiResultRerunLoading = (state: RootState) => state.meetingSummary.aiResults.isRerunLoading

export const selectAiResultPollingEnabled = (state: RootState) => state.meetingSummary.aiResults.pollingEnabled

export const selectSavedResultId = (state: RootState) => state.meetingSummary.aiResults.savedResultId

export const selectEditedAiResults = (state: RootState) => editedAiResultsSelectors.selectAll(state)
